import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import CardsList from "@/components/CardsList";
import {
	LineChart,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";

import {usePlayers} from "@/utils/hooks";

import "./index.scss";

const StatsContainer = (props) => {
	const {type, categories} = props;

	const {players} = usePlayers({
		setLoading: false,
		handleNotification: undefined,
		prospect: false,
		onlyInternal: true,
		goalkeepers: false,
	});

	const seasonId = useSelector((state) => state?.season?.id);

	const [category, setCategory] = useState();
	const [categoryPlayers, setCategoryPlayers] = useState([]);

	const [internalTeams, setInternalTeams] = useState([]);
	const getTeams = async () => {
		try {
			const response = await RosterAPI.list({seasonId, prospect: false});
			setInternalTeams(response);
		} catch (error) {
			console.log(error);
		}
	};
	useEffect(() => {
		if (seasonId) {
			getTeams();
		}
	}, [seasonId]);

	const [stats, setStats] = useState();
	const [statsDate, setStatsDate] = useState(new Date());

	const parseTqrValue = (value) => {
		if (value === null || value === undefined) {
			return null;
		}
		if (typeof value === "string" && value.indexOf("-") !== -1) {
			value = value.split(" - ")[0];
		}
		const parsed = parseFloat(value);
		return Number.isNaN(parsed) ? null : parsed;
	};

	const getTrendData = (stat) => {
		if (!stat?.items?.length) {
			return [];
		}
		if (type === "rpe") {
			return stat.items
				.filter((i) => i.rpeDate && !i.avgRpe)
				.map((i) => ({
					date: i.rpeDate,
					value: Number(i.trainingLoad || i.rpe || 0),
				}))
				.filter((i) => i.value > 0)
				.sort((a, b) => new Date(a.date) - new Date(b.date));
		}
		if (type === "tqr") {
			return stat.items
				.filter((i) => i.tqrDate)
				.map((i) => {
					const tqr = parseTqrValue(i.tqrValue);
					return {
						date: i.tqrDate,
						value: tqr !== null ? (tqr * 100) / 20 : null,
					};
				})
				.filter((i) => i.value !== null && i.value > 0)
				.sort((a, b) => new Date(a.date) - new Date(b.date));
		}
		return [];
	};

	const getAlert = (stat) => {
		if (!stat?.items?.length) {
			return null;
		}
		if (type === "rpe") {
			const totalItem = stat.items.find((i) => i.avgRpe);
			const avgRpe = totalItem?.avgRpe || 0;
			const avgLoad = totalItem?.trainingLoad || 0;

			if (avgLoad > 700 || avgRpe > 7) {
				return {label: "Carico alto", type: "danger"};
			}
			if (avgLoad > 300 || avgRpe > 5) {
				return {label: "Carico medio", type: "warning"};
			}
			return {label: "Carico basso", type: "success"};
		}

		if (type === "tqr") {
			const tqrValues = stat.items
				.map((i) => parseTqrValue(i.tqrValue))
				.filter((v) => v !== null);
			if (!tqrValues.length) {
				return null;
			}
			const avgTqr = tqrValues.reduce((sum, v) => sum + v, 0) / tqrValues.length;
			const percent = (avgTqr * 100) / 20;

			if (percent <= 64) {
				return {label: "Attenzione", type: "danger"};
			}
			if (percent <= 80) {
				return {label: "Buono", type: "warning"};
			}
			return {label: "Ottimo", type: "success"};
		}
		return null;
	};

	const getRPEStats = async () => {
		const selectedDate = moment(statsDate);
		const fromDate = selectedDate.startOf("isoWeek").format("YYYY-MM-DD");
		const toDate = selectedDate.endOf("isoWeek").format("YYYY-MM-DD");

		let promises = [];
		categoryPlayers.forEach((cp) => {
			promises.push(
				PlayerAPI.listRPE({
					playerId: cp.id,
					fromDate,
					toDate,
				}),
			);
		});

		try {
			let responses = await Promise.all(promises);
			responses = responses.map((r, k) => {
				const totalBox = {
					eventDuration: 0,
					avgRpe: 0,
					trainingLoad: 0,
				};

				let totalRpe = 0;
				r.forEach((rd) => {
					totalRpe += parseInt(rd.rpe);
					totalBox.eventDuration += rd.eventDuration;
					totalBox.trainingLoad += rd.trainingLoad;
				});
				totalBox.avgRpe = Math.round((totalRpe / r.length) * 100) / 100;

				return {
					player: categoryPlayers[k],
					items: [...r, totalBox],
				};
			});
			setStats(responses);
		} catch (error) {
			console.log(error);
		}
	};

	const getTQRStats = async () => {
		const fromDate = moment(statsDate).startOf("week").format("YYYY-MM-DD");
		const toDate = moment(statsDate).endOf("week").format("YYYY-MM-DD");

		let promises = [];
		categoryPlayers.forEach((cp) => {
			promises.push(PlayerAPI.playerListTQR(cp.id, {fromDate, toDate}));
		});

		try {
			let responses = await Promise.all(promises);
			responses = responses.map((r, k) => {
				return {
					player: categoryPlayers[k],
					items: r,
				};
			});
			setStats(responses);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		if (categoryPlayers?.length) {
			switch (type) {
				case "rpe":
					getRPEStats();
					break;
				case "tqr":
					getTQRStats();
					break;
				default:
					break;
			}
		}
	}, [type, categoryPlayers, statsDate]);

	useEffect(() => {
		if (category && players?.length) {
			const currentInternalTeam = internalTeams?.filter(
				(t) => t.playerCategoryId === category.value,
			)?.[0];

			setCategoryPlayers(currentInternalTeam?.players?.map((p) => p.player));
		} else {
			setStats([]);
			setCategoryPlayers([]);
		}
	}, [category, players]);

	const getListConfiguration = (item) => {
		switch (type) {
			case "rpe":
				return {
					data: item.items?.filter((i) => i.avgRpe || i.rpe > 0),
					customClassName: (item) => (item.avgRpe ? "total" : ""),
				};
			case "tqr":
				return {
					data: item.items?.filter((i) => parseInt(i.tqrValue) > 0),
				};
			default:
				return {};
		}
	};

	const getCardsList = (stat) => {
		let list = <></>;
		switch (type) {
			case "rpe":
				list = (
					<CardsList
						type="rpe_stat"
						listConfiguration={getListConfiguration(stat)}
						defaultSortedField="trainingDate"
						defaultSortedOrder="ascend"
						fitHeight
					/>
				);
				break;
			case "tqr":
				list = (
					<CardsList
						type="tqr_stat"
						listConfiguration={getListConfiguration(stat)}
						defaultSortedField="tqrDate"
						defaultSortedOrder="ascend"
						fitHeight
					/>
				);
				break;
			default:
				break;
		}

		return list;
	};

	return (
		<div className="sheet-container measurements-container">
			<div className="row mt-2">
				<div className="col-12">
					<VLFCard
						title="Filtri"
						openable={false}
						content={
							<div className="row no-margin">
								<div className="col-12 col-md-4">
									<VLFSelectNew
										options={categories
											.map((c) => {
												return {
													value: c.id,
													label: c.name,
												};
											})
											.filter((c) => c.label !== "Prima Squadra")}
										sorted={false}
										value={category}
										onChange={(newValue) => {
											setCategory(newValue);
										}}
										label="Categoria"
										required
									/>
								</div>
								<div className="col-12 col-md-4">
									<VLFDatePicker
										type="week"
										label="Settimana"
										customDateFormat="dd/MM/yyyy"
										value={
											new Date(moment(statsDate).startOf("week").isoWeekday(1))
										}
										onChange={setStatsDate}
										required
									/>
								</div>
							</div>
						}
					/>
				</div>
			</div>
			<div className="row players-stats mt-2">
				{stats
					?.filter((s) => {
						if (type === "rpe") {
							return s.items?.some((i) => i.avgRpe || i.rpe > 0);
						}
						if (type === "tqr") {
							return s.items?.some(
								(i) => parseTqrValue(i.tqrValue) !== null,
							);
						}
						return true;
					})
					?.sort((a, b) => {
						if (a.player.lastName < b.player.lastName) {
							return -1;
						}
						if (a.player.lastName > b.player.lastName) {
							return 1;
						}
						return 0;
					})
					?.map((s, k) => {
						const trendData = getTrendData(s);
						const alert = getAlert(s);
						return (
							<div className="col-12" key={k}>
								<div className="single-player-stat mb-2">
									<div className="player-header">
										<div className="player-name">
											{s.player?.firstName + " " + s.player?.lastName}
										</div>
										{alert && (
											<div className={`player-alert ${alert.type}`}>
												{alert.label}
											</div>
										)}
									</div>
									<div className="player-trend">
										{trendData.length ? (
											<ResponsiveContainer width="100%" height={80}>
												<LineChart data={trendData}>
													<XAxis dataKey="date" hide />
													<YAxis hide domain={["dataMin", "dataMax"]} />
													<Tooltip
														formatter={(value) =>
															type === "tqr"
																? `${Number(value).toFixed(0)}%`
																: Number(value).toFixed(0)
														}
														labelFormatter={(label) =>
															moment(label).format("DD/MM")
														}
													/>
													<Line
														type="monotone"
														dataKey="value"
														stroke={type === "tqr" ? "#22c55e" : "#2563eb"}
														strokeWidth={2.2}
														dot={false}
													/>
												</LineChart>
											</ResponsiveContainer>
										) : (
											<div className="player-trend-empty">Nessun dato</div>
										)}
									</div>
									{getCardsList(s)}
								</div>
							</div>
						);
					})}
			</div>
		</div>
	);
};

export default StatsContainer;
